import React, { useState } from 'react';
import Link from '../components/LocalizedLink';

import { ArrowLeft, Check, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import PackageRequestModal from '../components/PackageRequestModal';

const ConfiguratoreePage = () => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState({});
  const [showModal, setShowModal] = useState(false);
  
  const funzionalita = [
    { id: 'sito_base', iniziale: 10, mensile: 8 },
    { id: 'sito_esteso', iniziale: 25, mensile: 12 },
    { id: 'layout_html', iniziale: 15, mensile: 0 },
    { id: 'webapp', iniziale: 35, mensile: 15 },
    { id: 'catalogo', iniziale: 15, mensile: 5 },
    { id: 'prenotazioni', iniziale: 25, mensile: 10 },
    { id: 'ordini', iniziale: 30, mensile: 12 },
    { id: 'pagamenti', iniziale: 20, mensile: 8 },
    { id: 'notifiche', iniziale: 15, mensile: 5 },
    { id: 'ecommerce', iniziale: 80, mensile: 25 },
    { id: 'gestionale', iniziale: 50, mensile: 18 },
    { id: 'app_mobile', iniziale: 70, mensile: 20 },
    { id: 'integrazioni', iniziale: 30, mensile: 8 },
    { id: 'multilingua', iniziale: 20, mensile: 5 }
  ].map(f => ({ ...f, nome: t(`configuratore.items.${f.id}.nome`), desc: t(`configuratore.items.${f.id}.desc`) }));

  const toggleItem = (id) => {
    setSelected(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const calcTotale = () => {
    let iniziale = 0;
    let mensile = 0;
    
    funzionalita.forEach(f => {
      if (selected[f.id]) {
        iniziale += f.iniziale;
        mensile += f.mensile;
      }
    });
    
    if (mensile < 10 && Object.values(selected).some(v => v)) {
      mensile = 10;
    }
    
    return { iniziale, mensile };
  };

  const totale = calcTotale();
  const itemsSelezionati = Object.values(selected).filter(v => v).length;

  const getSelectedDetails = () => {
    const selectedFeatures = funzionalita.filter(f => selected[f.id]);
    return selectedFeatures.map(f => f.nome).join(', ');
  };

  const handleRequestPackage = () => {
    if (itemsSelezionati > 0) {
      setShowModal(true);
    }
  };

  return (
    <div data-testid="configuratore-page" className="min-h-screen bg-[var(--color-bg)]">
      <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
        <Link 
          to="/tariffe" 
          className="inline-flex items-center text-[var(--color-primary)] hover:underline mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.torna_pacchetti')}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[var(--color-text-muted)]/10 flex items-center justify-center">
              <Settings className="w-5 h-5 text-[var(--color-text-muted)]" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[var(--color-text)]">
              {t('configuratore.title')}
            </h1>
          </div>
          <p className="text-[var(--color-text-muted)] text-sm">
            {t('configuratore.subtitle')}
          </p>
        </motion.div>

        <div className="space-y-3 mb-8">
          {funzionalita.map((item, index) => {
            const isSelected = selected[item.id];
            
            return (
              <motion.button
                key={item.id}
                onClick={() => toggleItem(item.id)}
                className={`w-full p-4 rounded-xl border text-left transition-all ${
                  isSelected
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5'
                    : 'border-[var(--color-border)] bg-[var(--color-surface)] hover:border-[var(--color-border-hover)]'
                }`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                data-testid={`config-${item.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                    isSelected 
                      ? 'border-[var(--color-primary)] bg-[var(--color-primary)]' 
                      : 'border-[var(--color-border)]'
                  }`}>
                    {isSelected && <Check className="w-4 h-4 text-white" />}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <span className={`font-medium ${isSelected ? 'text-[var(--color-primary)]' : 'text-[var(--color-text)]'}`}>
                        {item.nome}
                      </span>
                      <span className="text-xs text-[var(--color-text-muted)] whitespace-nowrap">
                        +{item.iniziale}€ / +{item.mensile}€/m
                      </span>
                    </div>
                    <p className="text-xs text-[var(--color-text-muted)] mt-0.5">{item.desc}</p>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>

        <motion.div 
          className="sticky bottom-6 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-2xl p-5 shadow-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-[var(--color-text-muted)]">
                {itemsSelezionati === 1
                  ? t('configuratore.selected_one', { count: itemsSelezionati })
                  : t('configuratore.selected_other', { count: itemsSelezionati })}
              </p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-2xl font-bold text-[var(--color-text)]">{totale.iniziale}€</span>
                <span className="text-[var(--color-text-muted)]">+</span>
                <span className="text-xl font-bold text-[var(--color-primary)]">{totale.mensile}€</span>
                <span className="text-sm text-[var(--color-text-muted)]">{t('tariffe.per_month')}</span>
              </div>
            </div>
            
            <Button 
              className="btn-primary"
              disabled={itemsSelezionati === 0}
              onClick={handleRequestPackage}
              data-testid="request-custom-package-btn"
            >
              {t('configuratore.request_quote')}
            </Button>
          </div>
          
          <p className="text-xs text-[var(--color-text-muted)]">
            {t('configuratore.estimate_disclaimer')}
          </p>
        </motion.div>

        <div className="mt-12 p-4 bg-[var(--color-surface)] rounded-xl border border-[var(--color-border)]">
          <p className="text-sm text-[var(--color-text-muted)]">
            <strong className="text-[var(--color-text)]">{t('configuratore.note_prefix')}</strong> {t('configuratore.note_text')}{' '}
            <Link to="/tariffe" className="text-[var(--color-primary)] hover:underline">{t('configuratore.note_link')}</Link>.
          </p>
        </div>
      </div>

      <PackageRequestModal 
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        packageInfo={{
          nome: t('configuratore.custom_name'),
          iniziale: totale.iniziale,
          mensile: totale.mensile,
          dettagli: getSelectedDetails()
        }}
      />
    </div>
  );
};

export default ConfiguratoreePage;
